import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { SurveyService, SurveyData } from './survey.service';
import { UserIdService } from '../services/user-id/user-id.service';

export type PlannerSurveyScenario =
  | 'od'
  | 'line'
  | 'intercity'
  | 'station'
  | 'stationPoi';

export interface SurveyTriggerContext {
  currentPageId?: string | number;
  currentPagePath?: string;
  plannerDestinationId?: number;
  plannerLineId?: number;
  cityId?: string;
  plannerStationId?: number;
  hasTaxiCalculation?: boolean;
  /** Route-planner: which search flow produced the trigger */
  plannerScenario?: PlannerSurveyScenario;
  originLocationId?: string | number;
  destinationLocationId?: string | number;
  routeId?: string | number;
  originCityId?: string;
  destinationCityId?: string;
  stationId?: string | number;
}

@Injectable({
  providedIn: 'root',
})
export class SurveyTriggerService {
  private surveysCache$: Observable<SurveyData[]> | null = null;

  constructor(
    private readonly surveyService: SurveyService,
    private readonly userIdService: UserIdService
  ) {}

  private getAllSurveys(): Observable<SurveyData[]> {
    if (!this.surveysCache$) {
      this.surveysCache$ = this.surveyService.getAllSurveys().pipe(shareReplay(1));
    }
    return this.surveysCache$;
  }

  clearCache(): void {
    this.surveysCache$ = null;
  }

  getSurveysForContext(context: SurveyTriggerContext): Observable<SurveyData[]> {
    if (context.plannerScenario) {
      return this.getSurveysForPlannerScenario(context);
    }

    if (context.hasTaxiCalculation) {
      return this.getSurveysWithTaxiTrigger();
    }

    if (context.currentPageId || context.currentPagePath) {
      return this.getSurveysWithPageTrigger(
        context.currentPageId,
        context.currentPagePath
      );
    }

    return of([]);
  }

  private getSurveysForPlannerScenario(
    context: SurveyTriggerContext
  ): Observable<SurveyData[]> {
    return this.getAllSurveys().pipe(
      map((surveys) =>
        surveys.filter((survey): survey is SurveyData => {
          if (!this.surveyService.isSurveyActive(survey)) {
            return false;
          }
          if (this.userIdService.isPollCompleted(survey.id)) {
            return false;
          }
          return this.plannerScenarioMatches(survey, context);
        })
      )
    );
  }

  private plannerScenarioMatches(
    survey: SurveyData,
    context: SurveyTriggerContext
  ): boolean {
    const scenario = context.plannerScenario;
    switch (scenario) {
      case 'od':
        return this.matchesOdTriggers(survey, context);
      case 'line':
        return this.matchesLineTriggers(survey, context);
      case 'intercity':
        return this.matchesIntercityTriggers(survey, context);
      case 'station':
        return this.matchesStationTriggers(survey, context, false);
      case 'stationPoi':
        return this.matchesStationTriggers(survey, context, true);
      default:
        return false;
    }
  }

  private matchesOdTriggers(survey: SurveyData, context: SurveyTriggerContext): boolean {
    const triggers = survey.surveyTriggerPlannerDestinations as unknown[] | undefined;
    if (!triggers?.length) {
      return false;
    }
    const o = context.originLocationId;
    const d = context.destinationLocationId;
    return triggers.some((t) =>
      this.matchesPlannerDestinationTrigger(t, o, d)
    );
  }

  private matchesPlannerDestinationTrigger(
    trigger: unknown,
    originId?: string | number,
    destId?: string | number
  ): boolean {
    const tr = trigger as {
      allLocations?: boolean;
      locationId?: string | number;
    };
    if (tr.allLocations === true) {
      return true;
    }
    if (tr.locationId === undefined || tr.locationId === null) {
      return false;
    }
    return (
      this.idsEqual(tr.locationId, originId) || this.idsEqual(tr.locationId, destId)
    );
  }

  private matchesLineTriggers(survey: SurveyData, context: SurveyTriggerContext): boolean {
    const triggers = survey.surveyTriggerPlannerLines as unknown[] | undefined;
    if (!triggers?.length) {
      return false;
    }
    const routeId = context.routeId;
    return triggers.some((t) => {
      const tr = t as { allLineNumbers?: boolean; routeId?: string | number };
      if (tr.allLineNumbers === true) {
        return true;
      }
      return this.idsEqual(tr.routeId, routeId);
    });
  }

  private matchesIntercityTriggers(
    survey: SurveyData,
    context: SurveyTriggerContext
  ): boolean {
    const triggers = survey.surveyTriggerCities as unknown[] | undefined;
    if (!triggers?.length) {
      return false;
    }
    const o = context.originCityId;
    const d = context.destinationCityId;
    return triggers.some((t) => {
      const tr = t as { cityId?: string };
      if (tr.cityId === undefined || tr.cityId === null) {
        return false;
      }
      return this.idsEqual(tr.cityId, o) || this.idsEqual(tr.cityId, d);
    });
  }

  private matchesStationTriggers(
    survey: SurveyData,
    context: SurveyTriggerContext,
    poiList: boolean
  ): boolean {
    const triggers = survey.surveyTriggerPlannerStations as unknown[] | undefined;
    if (!triggers?.length) {
      return false;
    }
    if (poiList) {
      return triggers.some(
        (t) => (t as { allStations?: boolean }).allStations === true
      );
    }
    const sid = context.stationId;
    return triggers.some((t) => {
      const tr = t as { allStations?: boolean; stationId?: string | number };
      if (tr.allStations === true) {
        return true;
      }
      return this.idsEqual(tr.stationId, sid);
    });
  }

  private idsEqual(
    a: string | number | undefined | null,
    b: string | number | undefined | null
  ): boolean {
    if (a === undefined || a === null || b === undefined || b === null) {
      return false;
    }
    return String(a) === String(b);
  }

  private getSurveysWithPageTrigger(
    pageId?: string | number,
    pagePath?: string
  ): Observable<SurveyData[]> {
    return this.getAllSurveys().pipe(
      map((surveys) => {
        return surveys.filter((survey): survey is SurveyData => {
          if (!this.surveyService.isSurveyActive(survey)) {
            return false;
          }
          if (this.userIdService.isPollCompleted(survey.id)) {
            return false;
          }
          if (!survey.surveyTriggerPages || survey.surveyTriggerPages.length === 0) {
            return false;
          }
          const matchesPage = survey.surveyTriggerPages.some((trigger: unknown) => {
            const tr = trigger as {
              allPages?: boolean;
              page?: { id?: string | number };
              path?: string;
            };
            if (tr.allPages === true) {
              return true;
            }
            if (pageId && tr.page && tr.page.id !== undefined) {
              const triggerPageId = tr.page.id;
              if (
                triggerPageId === pageId ||
                triggerPageId === Number(pageId) ||
                String(triggerPageId) === String(pageId)
              ) {
                return true;
              }
            }
            if (pagePath && tr.path === pagePath) {
              return true;
            }
            return false;
          });
          return matchesPage;
        });
      })
    );
  }

  private getSurveysWithTaxiTrigger(): Observable<SurveyData[]> {
    return this.getAllSurveys().pipe(
      map((surveys) => {
        return surveys.filter((survey): survey is SurveyData => {
          if (!this.surveyService.isSurveyActive(survey)) {
            return false;
          }
          if (this.userIdService.isPollCompleted(survey.id)) {
            return false;
          }
          if (!survey.surveyTriggerTaxis || survey.surveyTriggerTaxis.length === 0) {
            return false;
          }
          return true;
        });
      })
    );
  }
}
